import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrganization } from "@/contexts/OrganizationContext";
import { DateRangePicker } from "./DateRangePicker";
import { useProjectsLookup, useUsersLookup } from "../api";

type FiltersBarProps = {
  startDate: Date;
  endDate: Date;
  onDateChange: (start: Date, end: Date) => void;
  userId: string;
  onUserChange: (id: string) => void;
  projectId: string;
  onProjectChange: (id: string) => void;
};

export function FiltersBar({
  startDate,
  endDate,
  onDateChange,
  userId,
  onUserChange,
  projectId,
  onProjectChange,
}: FiltersBarProps) {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const hasOrg = Boolean(currentOrganization?.id);
  const isOwner = hasOrg && currentOrganization?.role_id === "owner";
  const users = useUsersLookup(isOwner);
  const projects = useProjectsLookup(hasOrg);

  const gridCols = isOwner
    ? "grid grid-cols-1 gap-3 md:grid-cols-[minmax(340px,1fr)_repeat(2,minmax(200px,240px))]"
    : hasOrg
      ? "grid grid-cols-1 gap-3 md:grid-cols-[minmax(340px,1fr)_minmax(200px,240px)]"
      : "grid grid-cols-1 gap-3 md:grid-cols-[minmax(340px,1fr)]";

  return (
    <div className={gridCols}>
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={onDateChange}
      />

      {isOwner && (
        <Select value={userId} onValueChange={onUserChange}>
          <SelectTrigger className="h-10 w-full">
            <SelectValue placeholder={t("usageAndCost.filters.allUsers")} />
          </SelectTrigger>
          <SelectContent
            align="start"
            className="max-h-[320px] w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]"
          >
            <SelectItem value="all">
              {t("usageAndCost.filters.allUsers")}
            </SelectItem>
            {(users.data ?? []).map((u) => (
              <SelectItem key={u.value} value={u.value}>
                <span className="block max-w-full truncate">
                  {u.label || u.value}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {hasOrg && (
        <Select value={projectId} onValueChange={onProjectChange}>
          <SelectTrigger className="h-10 w-full">
            <SelectValue placeholder={t("usageAndCost.filters.allProjects")} />
          </SelectTrigger>
          <SelectContent
            align="start"
            className="max-h-[320px] w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]"
          >
            <SelectItem value="all">
              {t("usageAndCost.filters.allProjects")}
            </SelectItem>
            {(projects.data ?? []).map((p) => (
              <SelectItem key={p.value} value={p.value}>
                <span className="block max-w-full truncate">
                  {p.label || "—"}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
